import {
  Box,
  Button,
  ButtonAppearance,
  ButtonSize,
  Icon,
  IconSize,
  Pagination,
  Spinner,
  SpinnerSize,
  Text,
} from '@bechtel/gds';
import ky from 'ky';
import { FC, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  DocumentNavStyled,
  DocumentSectionStyled,
  DocumentViewStyled,
  EmptyDocumentContainerStyled,
  EmptyDocumentContentStyled,
  RangeSelectorStyled,
} from './styled';
// @ts-ignore: tsc declarations not recognising types/index.d.ts
import { ReactComponent as ViewDetailsSvg } from '@bechtel/client-assets/dist/images/view-details.svg';
import { Trans } from '@lingui/macro';
import 'react-pdf/dist/Page/TextLayer.css';

export interface DocumentViewerProps {
  url?: string;
  canDelete?: boolean;
  deleting?: false;
  onDelete?: () => void;
  isBook?: boolean;
  retryLoading: boolean;
  bookBuilderRetryCount: number;
  enableRetryDocumentLoading: (enable: boolean) => void;
}
pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();
export const DocumentViewer: FC<DocumentViewerProps> = ({
  url, 
  canDelete, 
  onDelete,
  deleting, 
  isBook = false, 
  retryLoading = false, 
  bookBuilderRetryCount = 0,
  enableRetryDocumentLoading = () => {}}
) => {
  const pdfDocumentRef = useRef<HTMLDivElement>(null);
  const printFrameRef = useRef<HTMLIFrameElement>(null);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [scale, setScale] = useState<number>(25);
  const [loadComplete, setLoadComplete] = useState<boolean>(false);
  const [printing, setPrinting] = useState<boolean>(false);
  const onDocumentLoadSuccess = async (document) => {
    setPage(1);
    setTotalPages(document.numPages);
    setLoadComplete(false);
  };
  const bookBuilderMaxRetryCount = 1
  //need to memoize for reference equality, else react-pdf Document re-renders on every cycle
  const file = useMemo(() => ({ url: url, withCredentials: !isBook }), [isBook, url]);

  const print = async () => {
    if (printing || !url) return;
    setPrinting(true);
    if (!printFrameRef || !printFrameRef.current) return;
    const pdfBlob = await ky
      .get(url, {
        credentials: isBook ? 'omit' : 'include',
      })
      .blob();
    const blob = new Blob([pdfBlob], { type: 'application/pdf' });
    const objectURL = URL.createObjectURL(blob);
    if (printFrameRef?.current) printFrameRef.current.src = objectURL;
    URL.revokeObjectURL(objectURL);
    setTimeout(function () {
      if (printFrameRef?.current?.contentWindow) printFrameRef.current.contentWindow.print();
      setPrinting(false);
    }, 1000);
  };

  //Determine the scale factor based on pdf document width
  const onPageLoadSuccess = (page) => {
    setLoadComplete(true);
    if (!(pdfDocumentRef && pdfDocumentRef.current)) {
      return;
    }
    const pageScale = (pdfDocumentRef ? pdfDocumentRef?.current?.clientHeight : 0) / page.originalHeight;
    if (scale !== pageScale && !loadComplete) {
      //-4 to allow for padding and avoid scroll on load
      setScale(pageScale * 100 - 4);
    }
  };
  return (
    <>
      {url ? (
        <>
          {loadComplete && (
            <DocumentNavStyled>
              <Pagination
                onPrev={() => {
                  setPage((prev) => {
                    if (prev <= 1) {
                      return 1;
                    }

                    return prev - 1;
                  });
                }}
                onNext={() => {
                  setPage((prev) => {
                    if (prev >= totalPages) {
                      return totalPages;
                    }

                    return prev + 1;
                  });
                }}
                currentPage={page.toString()}
                totalPages={totalPages}
                onPageChange={(page) => setPage(+page)}
              />
              <Box flexGrow={{ sm: '2' }} justifyContent={{ sm: 'flex-end' }} mr={{ sm: '500' }}>
                <Box mr={{ sm: '300' }}>
                  <Text>{Math.round(scale)}%</Text>
                </Box>
                <RangeSelectorStyled
                  min="1"
                  max="100"
                  step="1"
                  value={scale}
                  onChange={(event) => {
                    setScale(parseInt(event.target.value));
                  }}
                ></RangeSelectorStyled>
              </Box>

              {canDelete && (
                <Box ml={{ sm: '400' }}>
                  <Button
                    appearance={ButtonAppearance.SECONDARY}
                    size={ButtonSize.SMALL}
                    onClick={(event) => {
                      event.preventDefault();
                      onDelete && onDelete();
                    }}
                  >
                    <Box ml={{ sm: '100' }} mr={{ sm: '300' }}>
                      {deleting ? (
                        <Spinner></Spinner>
                      ) : (
                        <Icon icon="trash-alt" size={IconSize.MEDIUM} fillColor="blue.800" />
                      )}
                    </Box>

                    <Text size={'300'} color={'grey.1000'}>
                      <Trans> Delete </Trans>
                    </Text>
                  </Button>
                </Box>
              )}

              <Box ml={{ sm: '400' }}>
                <Button
                  appearance={ButtonAppearance.SECONDARY}
                  size={ButtonSize.SMALL}
                  onClick={(event) => {
                    event.preventDefault();
                    print();
                  }}
                  disabled={printing}
                >
                  <Box ml={{ sm: '100' }} mr={{ sm: '300' }}>
                    {printing ? <Spinner></Spinner> : <Icon icon="print" size={IconSize.MEDIUM} fillColor="blue.800" />}
                  </Box>

                  <Text size={'300'} color={'grey.1000'}>
                    <Trans> Print</Trans>
                  </Text>
                </Button>
              </Box>
              <Box ml={{ sm: '400' }}>
                <Button
                  appearance={ButtonAppearance.SECONDARY}
                  size={ButtonSize.SMALL}
                  onClick={async (event) => {
                    event.preventDefault();
                    const pdfBlob = await ky
                      .get(url, {
                        credentials: isBook ? 'omit' : 'include',
                      })
                      .blob();
                    const blob = new Blob([pdfBlob], { type: 'application/pdf' });
                    const objectURL = URL.createObjectURL(blob);
                    window.open(objectURL, 'popup_window', 'fullscreen=yes');
                    URL.revokeObjectURL(objectURL);
                  }}
                >
                  <Box ml={{ sm: '100' }} mr={{ sm: '300' }}>
                    <Icon icon="expand" size={IconSize.MEDIUM} fillColor="blue.800" />
                  </Box>
                  <Text size={'300'} color={'grey.1000'}>
                    <Trans>Full Screen</Trans>
                  </Text>
                </Button>
              </Box>
            </DocumentNavStyled>
          )}
          <DocumentSectionStyled id="pdfDocument" ref={pdfDocumentRef}>
            <DocumentViewStyled>
            {retryLoading && <Spinner size={SpinnerSize.LARGE}></Spinner>}
            {!retryLoading &&  <Document
                file={file}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={ (error) => {
                    if(isBook && error?.status === 403 && bookBuilderRetryCount < bookBuilderMaxRetryCount) { //re-enabling referesh for book builder only in case of 403 forbidden
                      enableRetryDocumentLoading(true);
                    }
                  } 
                }
                loading={<Spinner size={SpinnerSize.LARGE}></Spinner>}
              >
                <Page
                  pageNumber={page}
                  renderAnnotationLayer={false}
                  scale={scale / 100}
                  key={page}
                  loading={<Spinner size={SpinnerSize.LARGE}></Spinner>}
                  onLoadSuccess={onPageLoadSuccess}
                />
              </Document>}
            </DocumentViewStyled>
          </DocumentSectionStyled>
          <iframe ref={printFrameRef} style={{ display: 'none' }}></iframe>
        </>
      ) : (
        <EmptyDocumentContainerStyled>
          <EmptyDocumentContentStyled>
            <ViewDetailsSvg></ViewDetailsSvg>
            <Box mt={{ sm: '700' }}>
              <Text color="grey.600" size="400">
                <Trans> Select a document to preview.</Trans>
              </Text>
            </Box>
          </EmptyDocumentContentStyled>
        </EmptyDocumentContainerStyled>
      )}
    </>
  );
};
